"use client";

import LogoNight from "@/assets/images/logoDark.png";
import LogoDay from "@/assets/images/logoLight.png";
import {
  default as SimpleLogoDay,
  default as SimpleLogoNight,
} from "@/assets/images/logoSimple.png";
import { Icon, Image } from "@/components";
import { useAppUI } from "@/context/AppUIContext";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { DictionaryProps } from "@/types";
import { hasDashboardAccess, getUserData } from "@/utils/checkDashboardAccess";
import { useTheme } from "next-themes";
import { StaticImageData } from "next/image";
import { usePathname, useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  applyPermissions,
  getMenuItemsByType,
  getMenuType,
} from "./menuConfig";
import { MenuItemComponent } from "./MenuItem";
import * as S from "./styles";
import {
  getEstablishmentStatus,
  getInitialExpandedCategory,
  getLocaleFromPathname,
  getLocalizedPath,
  isCategoryExpanded,
  isEstablishmentActive,
  isPathActive,
} from "./utils";

const Layout: React.FC<DictionaryProps> = ({ dict }) => {
  const theme = useTheme();
  const { hasPermission, isSuperAdmin, session } = usePermission();

  const { sidebarOpen, toggleSidebar } = useAppUI();
  const pathname = usePathname();
  const router = useRouter();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const locale = useMemo(() => getLocaleFromPathname(pathname), [pathname]);

  const Logo = useMemo(
    () => (theme.theme === "dark" ? LogoNight : (LogoDay as StaticImageData)),
    [theme.theme],
  );
  const SimpleLogo = useMemo(
    () =>
      theme.theme === "dark"
        ? SimpleLogoNight
        : (SimpleLogoDay as StaticImageData),
    [theme.theme],
  );

  const isActive = useCallback(
    (path: string | undefined) => isPathActive(path, pathname, locale),
    [pathname, locale],
  );

  const getLocalizedPathMemo = useCallback(
    (path: string) => getLocalizedPath(path, locale),
    [locale],
  );

  const subscriptionsPath = useMemo(
    () => getLocalizedPathMemo("/settings/subscriptions"),
    [getLocalizedPathMemo],
  );

  const establishmentStatus = useMemo(
    () => getEstablishmentStatus(session),
    [session],
  );

  const isEstablishmentActiveStatus = useMemo(
    () => isEstablishmentActive(establishmentStatus),
    [establishmentStatus],
  );

  useEffect(() => {
    if (establishmentStatus === undefined) return;
    if (isEstablishmentActiveStatus) return;
    if (pathname === subscriptionsPath) return;

    router.replace(subscriptionsPath);
  }, [
    establishmentStatus,
    isEstablishmentActiveStatus,
    pathname,
    router,
    subscriptionsPath,
  ]);

  const menuType = useMemo(
    () => getMenuType(isSuperAdmin(), isEstablishmentActiveStatus),
    [isSuperAdmin, isEstablishmentActiveStatus],
  );

  const hasPermissionRef = useRef(hasPermission);
  hasPermissionRef.current = hasPermission;

  const hasPermissionStable = useCallback((permission: string) => {
    return hasPermissionRef.current(permission);
  }, []);

  // Verificar acesso ao dashboard
  const userData = useMemo(() => getUserData(session), [session]);
  const hasDashboardAccessValue = useMemo(
    () => hasDashboardAccess(userData),
    [userData],
  );

  const baseNavItems = useMemo(
    () =>
      getMenuItemsByType(menuType, {
        dict,
        hasPermission: hasPermissionStable,
        hasDashboardAccess: hasDashboardAccessValue,
      }),
    [menuType, dict, hasPermissionStable, hasDashboardAccessValue],
  );

  const navItems = useMemo(
    () => applyPermissions(baseNavItems, hasPermissionStable),
    [baseNavItems, hasPermissionStable],
  );

  const navItemsRef = useRef(navItems);
  const isActiveRef = useRef(isActive);

  navItemsRef.current = navItems;
  isActiveRef.current = isActive;

  const [expandedCategory, setExpandedCategory] = useState<string[]>([]);

  useEffect(() => {
    const newExpandedCategory = getInitialExpandedCategory(
      navItemsRef.current,
      isActiveRef.current,
    );
    setExpandedCategory((prev) => {
      if (prev.length !== newExpandedCategory.length) {
        return newExpandedCategory;
      }
      if (prev.length === 0 && newExpandedCategory.length === 0) {
        return prev;
      }
      if (prev[0] !== newExpandedCategory[0]) {
        return newExpandedCategory;
      }
      return prev;
    });
  }, [pathname]);

  // Permitir múltiplas abas abertas
  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategory((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  }, []);

  useEffect(() => {
    if (!sidebarOpen) {
      setExpandedCategory([]);
    }
  }, [sidebarOpen]);

  const handleNavigate = useCallback(() => {
    if (typeof window === "undefined") return;

    const isMobile = window.innerWidth < 992;

    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
  }, [sidebarOpen, toggleSidebar]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setExpandedCategory([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <S.Sidebar $isOpen={sidebarOpen} ref={sidebarRef}>
      <S.HeaderSidebar>
        <S.LogoLink $isOpen={sidebarOpen}>
          {sidebarOpen ? <Image src={Logo} /> : <Image src={SimpleLogo} />}
        </S.LogoLink>
        <S.ButtonNavigation onClick={toggleSidebar} $isOpen={sidebarOpen}>
          <Icon name="chevronRight" />
        </S.ButtonNavigation>
      </S.HeaderSidebar>
      <S.BoxNavigation>
        <S.ListNavigation>
          {navItems.map((item) => (
            <MenuItemComponent
              key={item.id}
              item={item}
              locale={locale}
              sidebarOpen={sidebarOpen}
              isActive={isActive}
              getLocalizedPath={getLocalizedPathMemo}
              isExpanded={isCategoryExpanded(
                item.id,
                expandedCategory,
                item,
                isActive,
              )}
              onToggle={toggleCategory}
              onNavigate={handleNavigate}
            />
          ))}
        </S.ListNavigation>
      </S.BoxNavigation>
    </S.Sidebar>
  );
};

export default Layout;
